import { useAuth } from '@teko/auth-react';
import { Form, styled } from '@teko/ds-desktop';
import { Area } from '@teko/location';
import { NewShipmentAddress } from 'components/NewShipmentAddress';
import { MODE_TERMINAL_PICKER, useDistanceTerminalContext } from 'contexts';
import { useLocationsV2 } from 'hooks';
import { useState } from 'react';
import { useGetDistanceTerminal } from '../useGetDistanceTerminal';
import { STEP_LOCATION, usePickLocation } from '../usePickLocation';
import { HeaderModal } from './HeaderModal';
import { EnterAddress } from './StepEnterAddress/EnterAddress';
import { PickAddress } from './StepEnterAddress/PickAddress';

export interface AddressForm {
  address: string;
  provinceCode: string;
  wardCode: string;
  districtCode: string;
  node: string;
}

export const ModeEnterAddressV2 = ({
  onClosePopup,
}: {
  onClosePopup: () => void;
}) => {
  const [form] = Form.useForm<AddressForm>();

  const { isGetCurrentLocation, setModeTerminalPicker } =
    useDistanceTerminalContext();
  const { user } = useAuth();

  const { getDistanceTerminalsByGeoLocation, getDistanceTerminalsByAddress } =
    useGetDistanceTerminal();

  const {
    provinces = [],
    wards = [],
    province,
    ward,
    loadingLocation,
    onProvinceChanged,
    onWardChanged,
  } = useLocationsV2();

  const [stepLocation, setStepLocation] = useState<STEP_LOCATION>('ADDRESS');
  const [place, setPlace] = useState<{ lat?: number; long?: number }>({
    lat: undefined,
    long: undefined,
  });

  const {
    headerText,
    backToPreviousLevelLocation,
    keyword,
    setKeyword,
    filterLocations,
  } = usePickLocation({
    stepLocation,
    location: { provinceName: province?.title || '' },
    setStepLocation,
    provinces,
    wards,
  });

  const displayBackButton = stepLocation !== 'ADDRESS';

  const resetPosition = () => {
    setPlace({ lat: undefined, long: undefined });
  };

  const onSelectLocation = (location: Area) => {
    if (stepLocation === 'PROVINCE') {
      onProvinceChanged(location.code);
      form.setFieldsValue({
        provinceCode: location.code,
        wardCode: '',
      });
      onWardChanged('');
      resetPosition();
      setStepLocation('ADDRESS');
      return;
    }
    onWardChanged(location.code);
    form.setFieldsValue({
      wardCode: location.code,
    });
    resetPosition();
    setStepLocation('ADDRESS');
  };

  const onFinishFormEnterAddress = (address: AddressForm) => {
    if (isGetCurrentLocation) {
      getDistanceTerminalsByGeoLocation();
      return;
    }

    const provinceName =
      provinces.find((province) => province.code === address.provinceCode)
        ?.title || '';
    const wardName =
      wards.find((ward) => ward.code === address.wardCode)?.title || '';
    getDistanceTerminalsByAddress({
      ...address,
      provinceName,
      wardName,
      lat: place.lat,
      long: place.long,
      node: address.node,
    });
  };

  return (
    <>
      <HeaderModal
        displayBackButton={displayBackButton}
        backToPreviousLevelLocation={backToPreviousLevelLocation}
        onClosePopup={onClosePopup}
        title={headerText.title}
        subTitle={headerText.sutTitle}
      />
      <StyledBody>
        {user?.userId ? (
          <NewShipmentAddress
            onClose={(refetch, newAddress) => {
              if (refetch) {
                const location = {
                  address: newAddress?.address || '',
                  wardName: newAddress?.wardName || '',
                  provinceName: newAddress?.provinceName || '',
                  wardCode: newAddress?.wardCode || '',
                  provinceCode: newAddress?.provinceCode || '',
                  name: newAddress?.name,
                  isDefault: newAddress?.isDefault,
                  telephone: newAddress?.telephone,
                  lat: Number.isNaN(Number(newAddress?.latitude))
                    ? undefined
                    : Number(newAddress?.latitude),
                  long: Number.isNaN(Number(newAddress?.longitude))
                    ? undefined
                    : Number(newAddress?.longitude),
                  note: newAddress?.note,
                };
                getDistanceTerminalsByAddress(location);
              } else {
                setModeTerminalPicker?.(MODE_TERMINAL_PICKER.LIST_ADDRESS);
              }
            }}
          />
        ) : (
          <>
            {stepLocation === 'ADDRESS' && (
              <EnterAddress
                province={province}
                ward={ward}
                setStepLocation={setStepLocation}
                form={form}
                setPlace={setPlace}
                onFinish={onFinishFormEnterAddress}
              />
            )}
            {stepLocation !== 'ADDRESS' && (
              <PickAddress
                placeHolderSearch={headerText.placeHolderSearch}
                keyword={keyword}
                setKeyword={setKeyword}
                filterLocations={filterLocations}
                loadingLocation={loadingLocation}
                onSelectLocation={onSelectLocation}
                selectedValue={
                  (stepLocation === 'PROVINCE' && province?.code) ||
                  (stepLocation === 'WARD' && ward?.code) ||
                  ''
                }
              />
            )}
          </>
        )}
      </StyledBody>
    </>
  );
};

const StyledBody = styled.div`
  margin-top: 16px;
`;
